import { useState } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { SelectChevronIcon } from '@/react/components/icons'
import { cn } from '@/lib/utils'

/**
 * Trigger height in px. The open list is bottom-aligned to the trigger's base by
 * offsetting it back down by exactly this much, so the two MUST move together —
 * for `side="top"` Radix places the list at `trigger.top - sideOffset`, which
 * only lands on `trigger.bottom` when the offset is the full trigger height.
 * Keep the matching `h-[32px]` class literal below so Tailwind's scanner sees it.
 */
const TRIGGER_HEIGHT_PX = 32

interface InlineSelectProps {
  value: number
  options: readonly number[]
  onChange: (value: number) => void
  className?: string
}

export function InlineSelect({ value, options, onChange, className }: InlineSelectProps) {
  const [open, setOpen] = useState(false)

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'flex items-center justify-center gap-2 h-[32px] px-4 py-2',
            'bg-off-white border border-dinnerplate-white rounded-input',
            'font-inter text-nav font-semibold leading-[1.5] text-off-black',
            'hover:border-grey transition-colors cursor-pointer',
            open && 'invisible',
            className
          )}
        >
          <span>{value}</span>
          <SelectChevronIcon className="text-off-black" />
        </button>
      </PopoverPrimitive.Trigger>

      {/* No Portal - renders inline so it appears as one unified window */}
      <PopoverPrimitive.Content
        side="top"
        align="center"
        sideOffset={-TRIGGER_HEIGHT_PX}
        // The footer always has room above it, and flipping to `bottom` would
        // drop the list off the bottom of the viewport — pin the placement so
        // opening never moves the box.
        avoidCollisions={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          'z-50 w-[var(--radix-popover-trigger-width)]',
          // p-2/gap-2/px-2/py-1 are Grain's exact-px 3px-scale keys (6px/6px/6px/3px
          // per Figma). Avoid `*-1.5`-style rem fallbacks here: the root font-size
          // is 14px, so `p-1.5` renders 5.25px and the box comes out short.
          'flex flex-col p-2',
          'bg-off-white border border-dinnerplate-white rounded-input',
        )}
      >
        {/* The gap belongs on the listbox, not on Content — Content has a single
            child, so a gap there spaces nothing and the rows render flush. */}
        <div role="listbox" aria-label="Select value" className="flex flex-col gap-2">
          {options.map((option) => (
            <button
              key={option}
              role="option"
              aria-selected={option === value}
              onClick={() => {
                onChange(option)
                setOpen(false)
              }}
              className={cn(
                'w-full px-2 py-1 rounded-xs cursor-pointer text-left',
                'font-inter text-sm transition-colors',
                option === value
                  ? 'bg-dinnerplate-white-light font-semibold text-off-black'
                  : 'font-normal text-grey hover:bg-dinnerplate-white-light'
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Root>
  )
}
